import { CalendarClock, CalendarPlus, ShieldAlert } from 'lucide-react';
import { Button } from 'antd';
import { Navigate } from 'react-router';
import { EmptyState } from '../../../../../shared/components/EmptyState';
import { RowListSkeleton } from '../../../../../shared/components/RowListSkeleton';
import { BookingDialogs } from '../components/BookingDialogs';
import { NextFreeWindowModal } from '../components/NextFreeWindowModal';
import { TruckScheduleList } from '../components/TruckScheduleList';
import { useBookingActions } from '../hooks/use-booking-actions';
import { useCanManageBookings } from '../hooks/use-can-manage-bookings';
import { useScheduleDialogs } from '../hooks/use-schedule-dialogs';
import { useServiceDueDate } from '../hooks/use-service-due-date';
import { useTruckSchedule } from '../hooks/use-truck-schedule';

/**
 * The Schedule tab of a truck's own page (gitaumoses4/chukua#169): its workshop bookings beside the trip it is committed to, in order, with
 * a booking over a trip flagged. Booking, moving and cancelling need `workorder.manage`; anyone who can see the tab can look for the
 * next free window, which only reads.
 */
export function TruckScheduleTab({ truckId }: { truckId: string }) {
  const schedule = useTruckSchedule(truckId);
  const actions = useBookingActions(schedule.reload);
  const dialogs = useScheduleDialogs();
  const canManage = useCanManageBookings();
  const dueDate = useServiceDueDate(truckId);

  if (schedule.state.status === 'forbidden') return <Navigate to="/" replace />;
  if (schedule.state.status === 'loading') return <RowListSkeleton label="Loading schedule" />;
  if (schedule.state.status === 'error') {
    return <EmptyState icon={ShieldAlert} title="Couldn't load the schedule" description="Something went wrong loading it. Try reloading the page." />;
  }

  const { entries } = schedule.state;
  const startBooking = canManage && (
    <Button type="primary" icon={<CalendarPlus className="size-4" />} onClick={() => dialogs.openCreate()}>
      Book into a workshop
    </Button>
  );

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="m-0 text-muted">The next 90 days: workshop bookings and the trip this truck is committed to. Services fit between jobs.</p>
        <div className="flex flex-wrap gap-2">
          <Button icon={<CalendarClock className="size-4" />} onClick={dialogs.openFinder}>
            Find the next free window
          </Button>
          {startBooking}
        </div>
      </div>
      {entries.length === 0 ? (
        <EmptyState icon={CalendarClock} title="Nothing scheduled" description="No workshop booking or trip in the next 90 days. The truck is free." />
      ) : (
        <TruckScheduleList entries={entries} onMove={canManage ? dialogs.openMove : null} onCancel={canManage ? dialogs.openCancel : null} />
      )}
      <BookingDialogs truckId={truckId} dialogs={dialogs} actions={actions} />
      <NextFreeWindowModal
        open={dialogs.isFinding}
        truckId={truckId}
        defaultDueBy={dueDate}
        onBook={canManage ? dialogs.bookFoundWindow : null}
        onClose={dialogs.closeFinder}
      />
    </div>
  );
}
